import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import LinkButton from 'components/layout/buttons/LinkButton';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TrashIcon from 'components/layout/icons/TrashIcon';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import translations from 'services/translations/translations';
import { fetchGroups } from 'store/products/actions';

import getProductGroupOptions from '../offerProduct/getProductGroupOptions';

import 'style/offers/OpportunityEstimates.css';

export interface IEstimateRow {
  amount: string;
  group: string;
  key: number;
  // The estimated contribution margin in percent, empty when not given
  marginPercent: string;
}

// The rows of the saved estimates of an opportunity
export const getEstimateRows = (
  estimates:
    | { amount: number; group: string; marginPercent?: null | number }[]
    | undefined
    | null
): IEstimateRow[] =>
  (estimates ?? []).map((estimate, index) => ({
    amount: formatGroupedNumber(estimate.amount, 2, 0).replace(/\s/g, ''),
    group: estimate.group,
    key: index + 1,
    marginPercent:
      estimate.marginPercent === null || estimate.marginPercent === undefined
        ? ''
        : formatGroupedNumber(estimate.marginPercent, 1, 0).replace(/\s/g, ''),
  }));

// The rows with a work type, as saved with the opportunity
export const getEstimatesPost = (
  rows: IEstimateRow[]
): { amount: number; group: string; marginPercent: null | number }[] =>
  rows
    .filter((row) => row.group !== '')
    .map((row) => ({
      amount: Math.max(0, numericStringToFloat(row.amount) || 0),
      group: row.group,
      marginPercent:
        row.marginPercent.trim() === '' ||
        Number.isNaN(numericStringToFloat(row.marginPercent))
          ? null
          : Math.min(
              100,
              Math.max(-100, numericStringToFloat(row.marginPercent))
            ),
    }));

/**
 * The estimated sums of an opportunity by work type (e.g. suspended ceilings installed):
 * rows added with "+", and the total of the opportunity below them. An opportunity has no
 * calculation.
 */
const OpportunityEstimates = ({
  children,
  locked,
  rows,
  save,
  setRows,
}: {
  // Shown below the total, before the save button (e.g. the links)
  children?: React.ReactNode;
  locked: boolean;
  rows: IEstimateRow[];
  // Saves the opportunity with its rows
  save?: () => void;
  setRows: (rows: IEstimateRow[]) => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.products]);
  const groups = useSelector((state: any) => state.products.groups);

  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  const tr = (key: string) => t(`${translations.offers}:opportunity.${key}`);

  const updateRow = (key: number, changes: Partial<IEstimateRow>) =>
    setRows(
      rows.map((row) => (row.key === key ? { ...row, ...changes } : row))
    );

  const addRow = () =>
    setRows([
      ...rows,
      {
        amount: '',
        group: '',
        key: Math.max(0, ...rows.map((row) => row.key)) + 1,
        marginPercent: '',
      },
    ]);

  const total = rows.reduce(
    (sum, row) => sum + (numericStringToFloat(row.amount) || 0),
    0
  );

  // The estimated margin of the whole opportunity: the margins of the rows weighted by
  // their sums, of the rows with a margin
  const withMargin = rows.filter(
    (row) =>
      row.marginPercent.trim() !== '' &&
      !Number.isNaN(numericStringToFloat(row.marginPercent))
  );
  const marginBase = withMargin.reduce(
    (sum, row) => sum + (numericStringToFloat(row.amount) || 0),
    0
  );
  const totalMargin =
    marginBase > 0
      ? withMargin.reduce(
          (sum, row) =>
            sum +
            (numericStringToFloat(row.amount) || 0) *
              numericStringToFloat(row.marginPercent),
          0
        ) / marginBase
      : null;

  return (
    <div className="opportunity-estimates">
      <p className="header-text">{tr('Estimates').toUpperCase()}</p>
      <p className="opportunity-estimates-info">{tr('Estimates info')}</p>
      {rows.length === 0 ? null : (
        <Row className="opportunity-estimates-header">
          <Col md={5}>{tr('Work type')}</Col>
          <Col md={3}>{tr('Estimated sum')}</Col>
          <Col md={3}>{tr('Estimated margin')}</Col>
        </Row>
      )}
      {rows.map((row) => (
        <Row key={row.key} className="opportunity-estimate-row">
          <Col md={5}>
            <SelectInputFieldControlled
              disabled={locked}
              id={`opportunity-estimate-group-${row.key}`}
              name={`opportunity-estimate-group-${row.key}`}
              onChange={(event: any) =>
                updateRow(row.key, { group: event.target.value })
              }
              options={getProductGroupOptions(groups, t)}
              value={row.group}
            />
          </Col>
          <Col md={3}>
            <NumberWithUnitInputField
              disabled={locked}
              id={`opportunity-estimate-amount-${row.key}`}
              name={`opportunity-estimate-amount-${row.key}`}
              onChange={(event: any) =>
                updateRow(row.key, { amount: event.target.value })
              }
              unit="€"
              value={row.amount}
            />
          </Col>
          <Col md={3}>
            <NumberWithUnitInputField
              disabled={locked}
              id={`opportunity-estimate-margin-${row.key}`}
              name={`opportunity-estimate-margin-${row.key}`}
              onChange={(event: any) =>
                updateRow(row.key, { marginPercent: event.target.value })
              }
              unit="%"
              value={row.marginPercent}
            />
          </Col>
          <Col md={1}>
            {locked ? null : (
              <TrashIcon
                style={{ marginTop: '0.5rem' }}
                onClick={() =>
                  setRows(rows.filter((other) => other.key !== row.key))
                }
              />
            )}
          </Col>
        </Row>
      ))}
      {locked ? null : (
        <LinkButton onClick={addRow}>{`+ ${tr('Add estimate')}`}</LinkButton>
      )}
      <Row className="opportunity-estimates-total">
        <Col md={5}>{tr('Total')}</Col>
        <Col md={3} className="opportunity-estimates-total-sum">
          {`${formatGroupedNumber(total)} €`}
        </Col>
        <Col md={3} className="opportunity-estimates-total-sum">
          {totalMargin === null
            ? ''
            : `${formatGroupedNumber(totalMargin, 1)} %`}
        </Col>
      </Row>
      {children}
      {locked || save === undefined ? null : (
        <div className="opportunity-estimates-actions">
          <PrimaryButton className="button-one-line" onClick={save}>
            {tr('Save estimates').toLocaleUpperCase()}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default OpportunityEstimates;
